/**
 * Purchase Registration Drawer Logic
 * Separated from affiliate logic to avoid conflicts.
 */

class PurchaseDrawer {
    constructor() {
        this.overlay = document.querySelector('.pd-js-overlay');
        this.drawer = document.querySelector('.purchase-drawer');
        this.views = document.querySelectorAll('.pd-view');
        this.currentStep = 1;
        this.state = {
            name: '',
            email: '',
            sendIdeas: false,
            keepSurprise: false,
            store: '',
            otherStore: '',
            orderType: 'known',
            orderNumber: '',
            giftMessage: '',
            address: {
                name: '',
                address1: '',
                address2: '',
                city: '',
                state: '',
                zip: '',
                country: 'US'
            },
            products: [] // Array of { id, title, img, qty }
        };

        this.init();
    }

    init() {
        if (!this.overlay) return;

        this.bindTriggers();
        this.bindInternalEvents();
        this.bindStepNavigation();
        this.bindProductSelector();
    }

    // 1. External Triggers (Delegated to document to catch dynamically added items)
    bindTriggers() {
        document.addEventListener('click', (e) => {
            const trigger = e.target.closest('.js-purchase-drawer-trigger');
            if (trigger) {
                e.preventDefault();
                const data = trigger.dataset;
                this.openWithProduct({
                    id: data.productId,
                    title: data.productTitle,
                    img: data.productImage,
                    qty: 1
                });
            }
        });
    }

    // 2. Open & Close
    openWithProduct(product) {
        this.state.products = [product];
        this.renderStep1();
        this.overlay.classList.add('is-active');
        document.body.classList.add('pd-drawer-open');
    }

    close() {
        this.overlay.classList.remove('is-active');
        document.body.classList.remove('pd-drawer-open');
        this.reset();
    }

    reset() {
        this.currentStep = 1;
        this.goToStep(1);
        // Clear inputs (basic version)
        this.overlay.querySelectorAll('input[type="text"], input[type="email"], textarea').forEach(el => el.value = '');
        this.overlay.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach(el => el.checked = false);
        this.overlay.querySelectorAll('.pd-pill').forEach(el => el.classList.remove('is-selected'));
    }

    // 3. Step Navigation
    bindInternalEvents() {
        this.overlay.querySelectorAll('.pd-js-close').forEach(btn => {
            btn.addEventListener('click', () => this.close());
        });

        // Close on overlay click
        this.overlay.addEventListener('click', (e) => {
            if (e.target === this.overlay) this.close();
        });

        // Char count
        const giftMsg = document.getElementById('pd-gift-message');
        const counter = document.querySelector('.pd-js-char-counter');
        if (giftMsg && counter) {
            giftMsg.addEventListener('input', () => {
                counter.textContent = `${giftMsg.value.length}/500`;
            });
        }

        // Pills
        this.overlay.querySelectorAll('.pd-pill').forEach(pill => {
            pill.addEventListener('click', () => {
                this.overlay.querySelectorAll('.pd-pill').forEach(p => p.classList.remove('is-selected'));
                pill.classList.add('is-selected');
                this.state.store = pill.dataset.store;

                const otherInput = this.overlay.querySelector('.pd-js-other-store');
                if (this.state.store === 'Other') {
                    otherInput.classList.remove('hidden');
                } else {
                    otherInput.classList.add('hidden');
                }
            });
        });
    }

    goToStep(step) {
        this.views.forEach(view => {
            view.classList.toggle('is-active', parseInt(view.dataset.step) === step);
        });
        this.currentStep = step;
    }

    bindStepNavigation() {
        this.overlay.querySelectorAll('.pd-js-next').forEach(btn => {
            btn.addEventListener('click', () => {
                const nextStep = parseInt(btn.dataset.next);
                if (this.validateStep(this.currentStep)) {
                    this.captureStepData(this.currentStep);
                    if (nextStep === 2) this.renderStep2();
                    if (nextStep === 3) this.renderStep3();
                    this.goToStep(nextStep);
                }
            });
        });

        const finalSave = this.overlay.querySelector('.pd-js-final-save');
        if (finalSave) {
            finalSave.addEventListener('click', () => this.handleFinalSubmission());
        }
    }

    // 4. Data Capture and Validation
    captureStepData(step) {
        if (step === 1) {
            this.state.name = document.getElementById('pd-name').value;
            this.state.email = document.getElementById('pd-email').value;
            this.state.sendIdeas = this.overlay.querySelector('[name="send_ideas"]').checked;
            this.state.keepSurprise = this.overlay.querySelector('[name="keep_surprise"]').checked;
        }
        if (step === 2) {
            this.state.orderType = this.overlay.querySelector('[name="order_type"]:checked').value;
            this.state.orderNumber = document.getElementById('pd-order-input').value;
            if (this.state.store === 'Other') {
                this.state.otherStore = document.getElementById('pd-store-name').value;
            }
        }
    }

    validateStep(step) {
        if (step === 1) {
            const name = document.getElementById('pd-name').value;
            const email = document.getElementById('pd-email').value;
            if (!name || !email) {
                alert('Please enter your name and email.');
                return false;
            }
        }
        if (step === 2) {
            if (!this.state.store) {
                alert('Please select where you purchased the gift.');
                return false;
            }
            if (this.state.store === 'Other' && !document.getElementById('pd-store-name').value) {
                alert('Please enter the store name.');
                return false;
            }
        }
        return true;
    }

    // 5. Render Step UI
    renderStep1() {
        const product = this.state.products[0];
        const card = this.overlay.querySelector('.pd-view-1 .pd-js-main-product');
        card.querySelector('.pd-js-img').src = product.img;
        card.querySelector('.pd-js-title').textContent = product.title;
    }

    renderStep2() {
        const product = this.state.products[0];
        const card = this.overlay.querySelector('.pd-view-2 .pd-js-main-product');
        card.querySelector('.pd-js-img').src = product.img;
        card.querySelector('.pd-js-title').textContent = product.title;
    }

    renderStep3() {
        // Summary data
        this.overlay.querySelector('.pd-js-summary-name').textContent = this.state.name;
        this.overlay.querySelector('.pd-js-summary-email').textContent = this.state.email;

        // Summary cards
        const container = this.overlay.querySelector('.pd-js-summary-cards');
        container.innerHTML = '';
        this.state.products.forEach(p => {
            container.innerHTML += `
        <div class="pd-drawer-card flex gap-4 items-center p-3">
          <img src="${p.img}" class="w-16 h-16 object-contain">
          <div class="flex-1 min-w-0">
            <div class="text-sm font-semibold truncate">${p.title}</div>
            <div class="text-xs text-gray-500">Qty: ${p.qty}</div>
          </div>
        </div>
      `;
        });
    }

    // 6. Product Selector
    bindProductSelector() {
        const selector = this.overlay.querySelector('.pd-js-selector');
        const openBtn = this.overlay.querySelector('.pd-js-open-selector');
        const closeBtn = this.overlay.querySelector('.pd-js-close-selector');
        const saveBtn = this.overlay.querySelector('.pd-js-save-selection');
        const searchInput = this.overlay.querySelector('.pd-js-search-input');

        if (openBtn) {
            openBtn.addEventListener('click', () => {
                selector.classList.add('is-active');
                this.syncSelectorState();
            });
        }

        if (closeBtn) {
            closeBtn.addEventListener('click', () => {
                selector.classList.remove('is-active');
            });
        }

        if (saveBtn) {
            saveBtn.addEventListener('click', () => {
                selector.classList.remove('is-active');
                this.renderStep2(); // Refresh list if needed (though step 2 only shows main product in current spec)
            });
        }

        // Toggle items in grid
        this.overlay.querySelectorAll('.pd-js-card').forEach(card => {
            card.addEventListener('click', () => {
                const id = card.dataset.id;
                const exists = this.state.products.find(p => p.id === id);

                if (exists) {
                    // Remove if not the original trigger item (optional: allow removing everything?)
                    this.state.products = this.state.products.filter(p => p.id !== id);
                    card.classList.remove('is-selected');
                } else {
                    this.state.products.push({
                        id: id,
                        title: card.dataset.title,
                        img: card.dataset.img,
                        qty: 1
                    });
                    card.classList.add('is-selected');
                }
                this.updateSelectedCount();
            });
        });

        // Search filter
        if (searchInput) {
            searchInput.addEventListener('input', (e) => {
                const query = e.target.value.toLowerCase();
                this.overlay.querySelectorAll('.pd-js-card').forEach(card => {
                    const title = card.dataset.title.toLowerCase();
                    card.style.display = title.includes(query) ? 'flex' : 'none';
                });
            });
        }
    }

    syncSelectorState() {
        this.overlay.querySelectorAll('.pd-js-card').forEach(card => {
            const isSelected = this.state.products.some(p => p.id === card.dataset.id);
            card.classList.toggle('is-selected', isSelected);
        });
        this.updateSelectedCount();
    }

    updateSelectedCount() {
        const count = this.state.products.length;
        this.overlay.querySelector('.pd-js-selected-count').textContent = `${count} item${count !== 1 ? 's' : ''} selected`;
    }

    // 7. Submission
    async handleFinalSubmission() {
        const btn = this.overlay.querySelector('.pd-js-final-save');
        btn.textContent = 'Submitting...';
        btn.disabled = true;

        // Capture final step data
        this.state.giftMessage = document.getElementById('pd-gift-message').value;
        const addressArea = this.overlay.querySelector('.pd-address-section');
        this.state.address.name = addressArea.querySelector('[name="address_name"]').value;
        this.state.address.address1 = addressArea.querySelector('[name="address_1"]').value;
        this.state.address.address2 = addressArea.querySelector('[name="address_2"]').value;
        this.state.address.city = addressArea.querySelector('[name="city"]').value;
        this.state.address.state = addressArea.querySelector('[name="state"]').value;
        this.state.address.zip = addressArea.querySelector('[name="zip"]').value;
        this.state.address.country = addressArea.querySelector('[name="country"]').value;

        const payload = {
            customer: {
                name: this.state.name,
                email: this.state.email,
                send_ideas: this.state.sendIdeas,
                keep_surprise: this.state.keepSurprise
            },
            purchase: {
                store: this.state.store === 'Other' ? this.state.otherStore : this.state.store,
                order_type: this.state.orderType,
                order_number: this.state.orderNumber,
                gift_message: this.state.giftMessage
            },
            products: this.state.products,
            thank_you_address: this.state.address,
            timestamp: new Date().toISOString()
        };

        console.log('Submission Payload:', payload);

        // Using Shopify Contact form as a proxy for the email notification
        // and potentially a custom endpoint if setup.
        // For this implementation, we will simulate the successful AJAX call.

        try {
            // Mocking a Metaobject submission via a Liquid Proxy or App if available
            // Since we can't create Metaobjects via JS Fetch without a private API/App,
            // we'll use the Shopify Contact Form method to ensure the admin gets the email.

            const formBody = new FormData();
            formBody.append('form_type', 'contact');
            formBody.append('utf8', '✓');
            formBody.append('contact[name]', payload.customer.name);
            formBody.append('contact[email]', payload.customer.email);
            formBody.append('contact[body]', `
          --- NEW GIFT RECORDED ---
          Products: ${payload.products.map(p => p.title).join(', ')}
          Store: ${payload.purchase.store}
          Order #: ${payload.purchase.order_number || 'Unknown'}
          Message: ${payload.purchase.gift_message}
          
          Thank You Address:
          ${payload.thank_you_address.name}
          ${payload.thank_you_address.address1}
          ${payload.thank_you_address.address2}
          ${payload.thank_you_address.city}, ${payload.thank_you_address.state} ${payload.thank_you_address.zip}
        `);

            await fetch('/contact', {
                method: 'POST',
                body: formBody
            });

            // Show Success
            this.renderSuccess();
        } catch (err) {
            console.error('Submission failed', err);
            btn.textContent = 'Error - try again';
            btn.disabled = false;
        }
    }

    renderSuccess() {
        const body = this.overlay.querySelector('.purchase-drawer__body');
        body.innerHTML = `
        <div class="pd-view is-active text-center py-20 px-8">
            <div class="pd-success-icon bg-green-100 text-green-600 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 text-3xl">✓</div>
            <h2 class="text-2xl font-bold mb-4">Purchase Recorded!</h2>
            <p class="text-gray-500 mb-8">Thank you for letting us know. The registry will be updated shortly.</p>
            <button type="button" class="pd-btn-primary pd-js-close">Back to registry</button>
        </div>
      `;
        body.querySelector('.pd-js-close').addEventListener('click', () => this.close());
    }
}

// Initialize on Load
document.addEventListener('DOMContentLoaded', () => {
    window.PDDrawer = new PurchaseDrawer();
});
